import React, { useState } from "react";
import { Button, Col, Form, Input, InputGroup, Label, Row } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPenToSquare } from "@fortawesome/free-solid-svg-icons";
import VendorPopup from "../../ThirdPartyAssessmentRequest/popups/vendorPopup";
import { toast } from "react-toastify";
import { saveThreatForAllVendorAssessments } from "../../../services/continuousMonitoringService";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { Popper, styled } from "@mui/material";
import { MDXEditor } from "@mdxeditor/editor";
import { ALL_PLUGINS } from "../../../utils/pluginsForMDXEditor";
import { hasValue } from "../../../utils/commonFns";

const StyledPopper = styled(Popper)({
  "& .MuiAutocomplete-listbox": {
    fontSize: "12px",
  },
});

const ThreatInitialState = {
  vendorId: 0,
  vendorName: "",
  threatType: "",
  title: "",
  details: "",
  howToResolve: "",
  references: "",
  impactedAssets: "",
  reportedOn: "",
};

export const ReportNewThreatTab = () => {
  const [threatState, setThreatState] = useState(ThreatInitialState);
  const [isVendorPopupOpen, setIsVendorPopupOpen] = useState(false);
  const [resetVersion, setResetVersion] = useState(0);

  const handleVendorAssignment = (vendor) => {
    setThreatState({
      ...threatState,
      vendorName: vendor.name,
      vendorId: vendor.id,
    });
  };

  const handleSave = async () => {
    if (!handleValidations()) {
      return;
    }
    const result = await saveThreatForAllVendorAssessments({
      vendorId: threatState.vendorId,
      reportedOn: threatState.reportedOn,
      threatType: threatState.threatType,
      title: threatState.title,
      details: threatState.details,
      howToResolve: threatState.howToResolve,
      references: threatState.references,
      impactedAssets: threatState.impactedAssets,
    });

    if (result) {
      toast("Threat has successfully reported to " + result + " Assessments", {
        type: toast.TYPE.SUCCESS,
        position: toast.POSITION.TOP_CENTER,
      });
      setThreatState(ThreatInitialState);
      setResetVersion((prev) => prev + 1);
    }
  };

  const handleValidations = () => {
    if (!hasValue(threatState.vendorName)) {
      toast("Please Select Vendor", {
        type: "error",
        position: toast.POSITION.TOP_CENTER,
      });
      return false;
    }

    if (!hasValue(threatState.threatType)) {
      toast("Please Enter Threat Type", {
        type: "error",
        position: toast.POSITION.TOP_CENTER,
      });
      return false;
    }

    if (!hasValue(threatState.title)) {
      toast("Please Enter Title", {
        type: "error",
        position: toast.POSITION.TOP_CENTER,
      });
      return false;
    }

    if (!hasValue(threatState.details)) {
      toast("Please Enter Details", {
        type: "error",
        position: toast.POSITION.TOP_CENTER,
      });
      return false;
    }

    if (!hasValue(threatState.reportedOn)) {
      toast("Please Enter Reported On", {
        type: "error",
        position: toast.POSITION.TOP_CENTER,
      });
      return false;
    }

    return true;
  };

  return (
    <>
      <Form inline>
        <Row>
          <Col md={12}>
            <div style={{ display: "flex" }}>
              <div style={{ marginLeft: "auto", marginRight: "20px" }}>
                <Button
                  className="ms-3 mb-2"
                  color="primary"
                  onClick={handleSave}
                >
                  Save Threat Details
                </Button>
                <Button
                  className="ms-3 mb-2"
                  color="secondary"
                  onClick={() => {
                    setThreatState(ThreatInitialState);
                    setResetVersion((prev) => prev + 1);
                  }}
                >
                  Reset
                </Button>
              </div>
            </div>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={4}>
            <Label required for="name" className="right-align required">
              Vendor
            </Label>
            <InputGroup>
              <Input value={threatState.vendorName} readOnly />
              <Button
                onClick={(e) => {
                  e.preventDefault();
                  setIsVendorPopupOpen(true);
                }}
              >
                <FontAwesomeIcon icon={faPenToSquare} />
              </Button>
            </InputGroup>
          </Col>
          <Col md={4}>
            <Label required for="ThreatType" className="right-align required">
              Threat Type
            </Label>
            <Autocomplete
              freeSolo
              id="ThreatType"
              options={["Critical Vulnerability", "Security Breach"]}
              disablePortal
              PopperComponent={StyledPopper}
              sx={{
                "& .MuiAutocomplete-inputRoot": {
                  fontSize: "14px", // Input font size
                  padding: "4px",
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      type: "search",
                    },
                  }}
                />
              )}
              inputValue={threatState.threatType}
              onInputChange={(event, newInputValue) => {
                setThreatState({
                  ...threatState,
                  threatType: newInputValue,
                });
              }}
              value={threatState.threatType}
              onChange={(event, newValue) => {
                setThreatState({
                  ...threatState,
                  threatType: newValue,
                });
              }}
            />
          </Col>
          <Col md={4}>
            <Label required for="reportedOn" className="right-align required">
              Reported On
            </Label>
            <Input
              type="date"
              value={threatState.reportedOn}
              onChange={(evt) => {
                setThreatState((pre) => ({
                  ...pre,
                  reportedOn: evt.target.value,
                }));
              }}
              name="reportedOn"
              id="reportedOn"
            />
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md={12}>
            <Label required for="Title" className="right-align required">
              Title
            </Label>
            <MDXEditor
              key={`${resetVersion}-0`}
              markdown={threatState.title}
              onChange={(value) =>
                setThreatState((pre) => ({
                  ...pre,
                  title: value,
                }))
              }
              plugins={ALL_PLUGINS}
            />
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={12}>
            <Label required for="Details" className="right-align required">
              Details
            </Label>
            <MDXEditor
              key={`${resetVersion}-1`}
              markdown={threatState.details}
              onChange={(value) =>
                setThreatState((pre) => ({
                  ...pre,
                  details: value,
                }))
              }
              plugins={ALL_PLUGINS}
            />
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={12}>
            <Label required for="HowToResolve" className="right-align required">
              How To Resolve
            </Label>
            <MDXEditor
              key={`${resetVersion}-2`}
              markdown={threatState.howToResolve}
              onChange={(value) =>
                setThreatState((pre) => ({
                  ...pre,
                  howToResolve: value,
                }))
              }
              plugins={ALL_PLUGINS}
            />
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={12}>
            <Label required for="References" className="right-align required">
              References
            </Label>
            <MDXEditor
              key={`${resetVersion}-3`}
              markdown={threatState.references}
              onChange={(value) =>
                setThreatState((pre) => ({
                  ...pre,
                  references: value,
                }))
              }
              plugins={ALL_PLUGINS}
            />
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={12}>
            <Label
              required
              for="ImpactedAssets"
              className="right-align required"
            >
              Impacted Assets
            </Label>
            <MDXEditor
              key={`${resetVersion}-4`}
              markdown={threatState.impactedAssets}
              onChange={(value) =>
                setThreatState((pre) => ({
                  ...pre,
                  impactedAssets: value,
                }))
              }
              plugins={ALL_PLUGINS}
            />
          </Col>
        </Row>
      </Form>
      {isVendorPopupOpen && (
        <VendorPopup
          {...{
            isVendorPopupOpen,
            setIsVendorPopupOpen,
            handleVendorAssignment,
          }}
        />
      )}
    </>
  );
};
